export {Terms as Page};

import {Helmet} from "react-helmet-async";
import messages from "../../messages/messages.js";

function Terms() {
    return (
        <section className="text-white">

            <Helmet>
                <title>{messages["SITE.NAME"]} | Terms of Service</title>
                <meta name="description" content={`The ${messages["SITE.NAME"]} Terms of Service`}/>
            </Helmet>

            <div className="tos">
                <h1>Terms of Service</h1>

                <p><strong>Effective Date:</strong> May 2, 2026<br/>
                    <strong>Last Updated:</strong> October 11, 2026</p>

                <p>
                    Welcome to {messages["DOMAIN"]} (the “Service”), operated by Unbroken Technology LLC d/b/a
                    The Nightcore Network (“we,” “us,” “our,”). These Terms of Service (“Terms”) govern
                    your access to and use of the Service. By accessing or using the Service,
                    you agree to be bound by these Terms and our Privacy Policy and Community
                    Guidelines, each of which is incorporated by reference.
                </p>

                <p><strong>
                    PLEASE READ CAREFULLY. THESE TERMS INCLUDE A BINDING ARBITRATION
                    AGREEMENT AND CLASS ACTION WAIVER IN SECTION 22 THAT AFFECT YOUR
                    LEGAL RIGHTS.
                </strong></p>

                <p>If you do not agree to these Terms, do not use the Service.</p>

                <h2>Quick Reference: How These Terms Are Organized</h2>

                <p>These Terms apply to everyone who uses the Service. Some sections apply only if you are in a
                    particular role:</p>

                <ul>
                    <li>Sections 1–9, 13–23: Apply to all users</li>
                    <li>Section 10: Applies to paid Subscribers</li>
                    <li>Section 11: Applies to Network Members and Academy Students</li>
                    <li>Section 12: Applies to Network Artists</li>
                </ul>

                <p>
                    If a section conflicts with a section that applies to your specific role,
                    the role-specific section controls for that role.
                </p>

                <h2>1. Eligibility and Account Registration</h2>

                <h3>1.1 Eligibility</h3>

                <p>
                    You must be at least 13 years old to use the Service. If you are under 18
                    (or the age of majority in your jurisdiction), you represent that you have
                    your parent or legal guardian’s permission to use the Service and that they
                    have read and agreed to these Terms on your behalf.
                </p>

                <p>
                    If you are using the Service on behalf of a company, organization, or other
                    legal entity, you represent that you have the authority to bind that entity
                    to these Terms.
                </p>

                <h3>1.2 Account Registration</h3>

                <p>To access certain features, you must create an account. When registering, you agree to:</p>

                <ul>
                    <li>Provide accurate, current, and complete information</li>
                    <li>Maintain and promptly update your account information</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Accept responsibility for all activity that occurs under your account</li>
                    <li>Notify us immediately of any unauthorized use of your account</li>
                </ul>

                <p>
                    You may not create more than one account, transfer your account to another
                    person, or use another user’s account without permission.
                </p>

                <h3>1.3 Username Selection</h3>

                <p>Usernames must comply with our Community Guidelines. We reserve the right to reclaim, modify, or
                    remove usernames that:</p>

                <ul>
                    <li>Infringe a third party’s intellectual property rights</li>
                    <li>Impersonate another person or entity</li>
                    <li>Are misleading, deceptive, or fraudulent</li>
                    <li>Contain hateful, harassing, or sexually explicit content</li>
                    <li>Violate these Terms or applicable law</li>
                </ul>

                <h2>2. Description of the Service</h2>

                <p>
                    The Service is an online platform for the {messages["SITE.NAME"]} music community.
                    Features include, without limitation:
                </p>

                <ul>
                    <li>Song Discovery</li>
                    <li>Embedded Playback</li>
                    <li>Custom Playlists</li>
                    <li>{messages["SITE.NAME"]} Radio</li>
                    <li>Quality Reporting</li>
                    <li>Subscriber Features</li>
                    <li>Network Features</li>
                    <li>Discord Community</li>
                </ul>

                <p>
                    We may modify, add, suspend, or discontinue any aspect of the Service at any
                    time, with or without notice.
                </p>

                <h3>2.1 The Service Depends on YouTube</h3>

                <p>
                    The Service relies on YouTube’s embedded video player for music playback and
                    on YouTube video metadata for content ingestion.
                </p>

                <ul>
                    <li>YouTube is a third-party service operated by Google LLC</li>
                    <li>We do not host or control audio or video content played through YouTube</li>
                    <li>YouTube content may become unavailable at any time</li>
                    <li>Changes to YouTube may affect Service functionality</li>
                    <li>We do not endorse or guarantee third-party content</li>
                </ul>

                <h3>2.2 YouTube Terms of Service</h3>

                <p>
                    The Service uses YouTube API Services. By using the Service, you agree to be bound by
                    the <a target="_blank" rel="noopener noreferrer" href="https://www.youtube.com/t/terms">YouTube
                    Terms of Service</a>. Information collected through YouTube API Services is handled as described
                    in our <a href="/privacy">Privacy Policy</a> and the <a target="_blank" rel="noopener noreferrer"
                    href="https://www.google.com/policies/privacy">Google Privacy Policy</a>.
                </p>

                <h2>3. User Roles</h2>

                <h3>3.1 Members</h3>
                <p>Members are non-paying users who can browse the Service, listen to music, and create playlists.</p>

                <h3>3.2 Subscribers</h3>
                <p>Subscribers are paying users who receive additional features.</p>

                <h3>3.3 Network Members</h3>
                <p>Network Members are accepted {messages["SITE.NAME"]} creators in the creator network.</p>

                <h3>3.4 Academy Students</h3>
                <p>Academy Students are creators accepted into the Network Academy.</p>

                <h3>3.5 Network Artists</h3>
                <p>Network Artists allow Network Members to use their music in {messages["SITE.NAME"]} videos.</p>

                <h3>3.6 Account Closure by You</h3>

                <p>You may close your account at any time by contacting
                    our support team at <a href="mailto:support@nightcore.com">support@nightcore.com</a>.</p>

                <ul>
                    <li>Your profile and uploads may be removed</li>
                    <li>Your playlists may be removed</li>
                    <li>Your Subscription will end at the current billing period</li>
                    <li>Your Network role will be removed</li>
                </ul>

                <h2>4. User Content and Licensing</h2>

                <h3>4.1 What Is User Content</h3>

                <p>
                    “User Content” means any content you provide to or through the Service,
                    including usernames, uploaded images, playlists, and feedback.
                </p>

                <h3>4.2 License You Grant to Us</h3>

                <p>
                    You retain ownership of your User Content. By submitting User Content, you
                    grant us a worldwide, non-exclusive, royalty-free license to use it solely
                    to operate and promote the Service.
                </p>

                <h3>4.3 Your Representations and Warranties</h3>

                <ul>
                    <li>You own or have rights to your User Content</li>
                    <li>Your User Content does not infringe third-party rights</li>
                    <li>Your User Content complies with law and these Terms</li>
                </ul>

                <h3>4.4 Prohibited Content</h3>

                <ul>
                    <li>Infringing content</li>
                    <li>Unlawful or abusive content</li>
                    <li>Sexually explicit material involving minors</li>
                    <li>Malware or harmful code</li>
                    <li>Impersonation or fraud</li>
                </ul>

                <h3>4.5 Content Removal</h3>

                <p>
                    We reserve the right to review or remove User Content at our discretion.
                </p>

                <h2>5. Acceptable Use</h2>

                <p>You agree not to:</p>

                <ul>
                    <li>Use the Service unlawfully</li>
                    <li>Violate third-party terms</li>
                    <li>Use bots or scrapers without permission</li>
                    <li>Reverse engineer the Service</li>
                    <li>Harass or harm users</li>
                    <li>Train AI models using the Service without permission</li>
                    <li>Create infringing content using Service tools</li>
                </ul>

                <h2>6. Discord Server as Part of the Service</h2>

                <p>
                    We operate a Discord server as part of the Service. Conduct in the server is
                    treated as conduct on the Service.
                </p>

                <h2>7. Subscriber Custom Subdomains and Subscriber Content</h2>

                <h3>7.1 Custom Subdomains</h3>

                <p>
                    Subscribers may receive a custom subdomain. Subdomains are licensed, not owned.
                </p>

                <ul>
                    <li>Subdomains may be revoked or reassigned</li>
                    <li>You may not sell or transfer your subdomain</li>
                    <li>Availability is subject to our policies</li>
                </ul>

                <h3>7.2 Subdomain Page Content</h3>

                <p>
                    Subscribers may customize their subdomain page with background images and playlists.
                </p>

                <h3>7.3 Custom YouTube Channel Artwork</h3>

                <p>
                    We may create custom artwork for Subscribers. We retain intellectual property rights unless agreed
                    otherwise in writing.
                </p>

                <h3>7.4 Promotional Placements</h3>

                <ul>
                    <li>Ads are non-refundable</li>
                    <li>We do not guarantee engagement metrics</li>
                    <li>Ads may be rejected or removed at our discretion</li>
                </ul>

                <h2>8. Intellectual Property</h2>

                <h3>8.1 Our Intellectual Property</h3>

                <p>
                    The Service and all related materials are owned by us or our licensors and
                    protected by intellectual property laws.
                </p>

                <h3>8.2 Feedback</h3>

                <p>
                    Feedback you provide may be used by us without compensation or attribution.
                </p>

                <h2>9. Copyright Policy and DMCA</h2>

                <h3>9.1 Repeat Infringer Policy</h3>

                <p>
                    We may terminate accounts of repeat copyright infringers.
                </p>

                <h3>9.2 DMCA Notices</h3>

                <p>
                    If you believe content infringes your copyright, you may submit a DMCA notice
                    to our designated agent.
                </p>

                <h2>10. Subscriptions and Billing</h2>

                <h3>10.1 Paid Subscriptions</h3>

                <p>
                    Certain features require a paid Subscription. Pricing and billing cycles are
                    described at checkout.
                </p>

                <h3>10.2 Automatic Renewal</h3>

                <p><strong>
                    YOUR SUBSCRIPTION WILL AUTOMATICALLY RENEW UNLESS YOU CANCEL.
                </strong></p>

                <ul>
                    <li>Subscriptions renew automatically</li>
                    <li>Your payment method will be charged recurring fees</li>
                    <li>Price changes will be communicated in advance</li>
                </ul>

                <h3>10.2 Cancellation</h3>

                <p>
                    You may cancel your Subscription through your account settings or by email.
                </p>

                <h3>10.3 Refunds</h3>

                <p>
                    Except where required by law, all fees are non-refundable.
                </p>

                <h2>11. Network Membership and Academy</h2>

                <p>
                    Network Membership and Academy participation are free programs subject to application and
                    acceptance.
                </p>

                <h3>11.3 Mutual Promotion Arrangement</h3>

                <p>
                    Network Members agree to include a link to {messages["DOMAIN"]} in new YouTube video descriptions.
                </p>

                <h3>11.4 Featuring Videos and Auto-Submission</h3>

                <p>
                    Eligible creators may enable automatic video imports through designated hashtags.
                </p>

                <h2>12. Network Artists</h2>

                <p>
                    Network Artists are governed by a separate Network Artist Addendum.
                </p>

                <h2>13. Privacy</h2>

                <p>
                    Your use of the Service is governed by our Privacy Policy, which describes how we handle
                    monitoring, browser tracing, and session replay data collected through tools such as Sentry.
                </p>

                <h2>14. Third-Party Services and Links</h2>

                <p>
                    The Service integrates with third-party services such as YouTube, Discord, and Sentry.
                </p>

                <p>
                    Sentry is used for error monitoring, performance tracing, and session replay. We configure it to
                    remove or redact sensitive and personal data before transmission, and it is not intended to retain
                    personal data. Use of third-party services is also subject to their own terms and privacy
                    policies.
                </p>

                <h2>15. Termination and Suspension</h2>

                <p>
                    We may suspend or terminate access to the Service at any time.
                </p>

                <h2>16. Changes to These Terms</h2>

                <p>
                    We may modify these Terms at any time with notice where required.
                </p>

                <h2>17. Notices</h2>

                <p>
                    Notices may be provided by email, through the Service, or through Discord.
                </p>

                <h2>18. Disclaimers</h2>

                <p><strong>
                    THE SERVICE IS PROVIDED “AS IS” WITHOUT WARRANTIES OF ANY KIND.
                </strong></p>

                <h2>19. Limitation of Liability</h2>

                <p><strong>
                    TO THE FULLEST EXTENT PERMITTED BY LAW, WE WILL NOT BE LIABLE FOR INDIRECT,
                    INCIDENTAL, OR CONSEQUENTIAL DAMAGES.
                </strong></p>

                <h2>20. Indemnification</h2>

                <p>
                    You agree to indemnify and hold harmless The Nightcore Network and its affiliates from claims arising from your
                    use of the Service.
                </p>

                <h2>21. Relationship Between Users</h2>

                <p>
                    We are not a party to arrangements between users of the Service.
                </p>

                <h2>22. Dispute Resolution and Arbitration</h2>

                <p><strong>
                    DISPUTES MUST BE RESOLVED THROUGH BINDING INDIVIDUAL ARBITRATION.
                </strong></p>

                <h3>22.3 Class Action Waiver</h3>

                <p>
                    You and we agree not to participate in class actions.
                </p>

                <h2>23. General</h2>

                <h3>23.1 Governing Law</h3>

                <p>
                    These Terms are governed by the laws of the State of Kentucky.
                </p>

                <h3>23.2 Entire Agreement</h3>

                <p>
                    These Terms constitute the entire agreement between you and us regarding the Service.
                </p>

                <h3>23.3 Contact</h3>

                <p>
                    For questions about these Terms, contact us at <a href="mailto:support@nightcore.com">support@nightcore.com</a>.
                </p>
            </div>
        </section>
    );
}
